import { useSyncExternalStore } from 'react';
import { readJson, writeJson } from '@/lib/storage';

/** Visitor's appearance choice. The brand look is dark, so that is the default until they pick. */
export type ThemeMode = 'dark' | 'light' | 'system';
export type ColorScheme = 'dark' | 'light';

/** Also read by the inline script in index.html that sets the scheme before first paint. */
const KEY = 'theme-mode';
const MODES: ThemeMode[] = ['dark', 'light', 'system'];
const listeners = new Set<() => void>();
const systemLight = typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: light)') : null;

let current: ThemeMode = (() => {
  const saved = readJson<ThemeMode | null>(KEY, null);
  return saved && MODES.includes(saved) ? saved : 'dark';
})();

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  systemLight?.addEventListener('change', fn);
  return () => {
    listeners.delete(fn);
    systemLight?.removeEventListener('change', fn);
  };
};

export function setThemeMode(mode: ThemeMode): void {
  current = mode;
  writeJson(KEY, mode);
  listeners.forEach((fn) => fn());
}

const resolve = (mode: ThemeMode): ColorScheme => (mode === 'system' ? (systemLight?.matches ? 'light' : 'dark') : mode);

/** The chosen mode and the scheme it resolves to right now (follows the OS when the mode is «system»). */
export function useThemeMode(): { mode: ThemeMode; scheme: ColorScheme } {
  const mode = useSyncExternalStore(subscribe, () => current, () => current);
  const scheme = useSyncExternalStore(subscribe, () => resolve(current), () => 'dark' as ColorScheme);
  return { mode, scheme };
}
